import React, { useState, useEffect, FC } from 'react'
import { Heading, Button, ButtonType, Modal, Text, Icon } from '@saasalerts/ui'
import { faClock } from '@awesome.me/kit-e270131d0a/icons/classic/regular'
import { useIdleTimer } from 'react-idle-timer'
import useUser from '@/hooks/useUser'
import { padStart } from 'lodash'
import {
  SESSION_TIMEOUT_IDLE_PROMPT_SECONDS,
  SESSION_TIMEOUT_IDLE_SECONDS,
} from '@/utils/constants'

const maxIdleMS = SESSION_TIMEOUT_IDLE_SECONDS * 1000
const promptBeforeIdleMS = SESSION_TIMEOUT_IDLE_PROMPT_SECONDS * 1000

const remainingTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${padStart(minutes.toString(), 2, '0')}:${padStart(
    remainingSeconds.toString(),
    2,
    '0'
  )} ${minutes == 1 && !remainingSeconds ? 'minute' : 'minutes'}.`
}

export const SessionTimeout: FC<React.PropsWithChildren> = () => {
  const { logout, mutate, isLoggedOut, isLoading } = useUser()
  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false)
  const [remaining, setRemaining] = useState<number>(maxIdleMS)

  const handleLogout = async () => {
    pause()
    if (!isLoggedOut) {
      await logout()
      mutate()
    }
  }

  const onSessionExpire = () => {
    setIsSessionExpired(true)
    setDisplayModal(true)
    handleLogout()
  }

  const handleDisplayModal = () => {
    setIsSessionExpired(false)
    setDisplayModal(true)
  }

  const { getRemainingTime, start, pause } = useIdleTimer({
    onIdle: onSessionExpire,
    onPrompt: handleDisplayModal,
    timeout: maxIdleMS,
    crossTab: true,
    promptBeforeIdle: promptBeforeIdleMS,
    name: 'session-timeout',
    syncTimers: 200,
    eventsThrottle: 500,
    leaderElection: true,
    startManually: true,
    throttle: 500,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000))
      if (getRemainingTime() > promptBeforeIdleMS && !isLoggedOut) {
        setDisplayModal(false)
      }
      if (isLoggedOut && !isSessionExpired) {
        setIsSessionExpired(true)
      }
    }, 500)
    return () => {
      clearInterval(interval)
    }
  })

  const handleContinueSession = () => {
    start()
    setDisplayModal(false)
  }

  const handleLogoutImmediatelyButton = () => {
    handleLogout()
    setDisplayModal(false)
    setIsSessionExpired(false)
    sessionStorage.clear()
  }

  useEffect(() => {
    if (isLoggedOut || isLoading) {
      pause()
      return
    }
    if (!isLoggedOut && !isLoading) {
      start()
      setDisplayModal(false)
      setIsSessionExpired(false)
    }
  }, [isLoggedOut, isLoading])

  const formattedRemainingTime = remainingTime(Math.max(remaining, 0))
  return (
    <>
      <Modal open={displayModal} setOpen={() => setDisplayModal(false)}>
        <Modal.Header>
          <Modal.TitleBar>
            <Icon icon={faClock} />
            <Heading type="h2">Session Timeout</Heading>
          </Modal.TitleBar>
        </Modal.Header>
        <Modal.Body>
          {isSessionExpired ? (
            <Text>
              Your session has expired. You will be redirected to the Login
              page.
            </Text>
          ) : (
            <Text>
              Your online session will expire in
              <span className="mx-1 text-red-600">
                {formattedRemainingTime}
              </span>
              Please click “Continue” to keep working or click "Log Off" to end
              your session now.
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isSessionExpired ? (
            <Button onClick={() => setDisplayModal(false)}>Ok</Button>
          ) : (
            <>
              <Button
                kind={ButtonType.OUTLINE_WHITE}
                onClick={handleLogoutImmediatelyButton}
              >
                Logoff
              </Button>
              <Button onClick={handleContinueSession}>Continue</Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  )
}
